import React from 'react'
import cn from 'classnames'
import s from './Newsletter.module.scss'
import { TFunction, useTranslation } from 'next-i18next/pages'
import { ToastType, useUI } from '@corratech/pylot-ui/context'
import { Button } from '@corratech/pylot-ui'
import { useSubscribeEmailToNewsletter } from './hooks/use-subscribe-email-to-newsletter'
import { isDateValid } from '@lib/utils/formValidators'
import { validate } from 'email-validator'
import { FormInput } from '@components/common/FormInput'
import {
    SubscribeFormElements,
    SubscribeFormErrors,
    ValidateResponse
} from './types'
import { SubscriptionStatusesEnum } from '@pylot-data/pylotschema'

const validateForm = (
    { name, lastName, email, birthday }: SubscribeFormErrors,
    t: TFunction
): ValidateResponse => {
    const errors = {} as SubscribeFormErrors

    if (!email) {
        errors.email = t('authErrors|Required')
    } else if (!validate(email)) {
        errors.email = t('authErrors|Invalid Email')
    }

    if (!name) {
        errors.name = t('authErrors|Required')
    }

    if (!lastName) {
        errors.lastName = t('authErrors|Required')
    }

    if (!isDateValid(birthday)) {
        errors.birthday = t('authErrors|Invalid BirthDate')
    }

    return { errors, hasErrors: Object.keys(errors).length !== 0 }
}

type NewsletterProps = {
    hideHeading?: boolean
}

const Newsletter = ({ hideHeading = false }: NewsletterProps): React.ReactElement => {
    const { t } = useTranslation(['common'])
    const { isLoading, subscribeEmailNewsletter } =
        useSubscribeEmailToNewsletter()
    const { openToast } = useUI()

    const [name, setName] = React.useState('')
    const [lastName, setLastName] = React.useState('')
    const [birthday, setBirthday] = React.useState('')
    const [email, setEmail] = React.useState('')
    const [buttonDisabled, setButtonDisabled] = React.useState(false)
    const [dirty, setDirty] = React.useState(false)
    const [formErrors, setFormErrors] = React.useState<SubscribeFormErrors>(
        {} as SubscribeFormErrors
    )

    const handleBirthdayChange = (date: string) => {
        const cleanDate = date.replace('/', '')
        if (cleanDate.length < 2) setBirthday(date)
        else setBirthday(`${cleanDate.slice(0, 2)}/${cleanDate.slice(2, 4)}`)
    }

    const handleSubscribe = async (
        e: React.FormEvent<SubscribeFormElements>
    ) => {
        e.preventDefault()
        const { errors, hasErrors } = validateForm(
            {
                name,
                lastName,
                email,
                birthday
            },
            t
        )

        if (!hasErrors) {
            try {
                const response = await subscribeEmailNewsletter({
                    email,
                    name,
                    lastName,
                    birthday
                })

                if (response?.errors?.length) {
                    openToast(
                        t(response?.errors[0]?.message),
                        ToastType.Danger,
                        true
                    )
                }

                if (response?.data?.subscribeEmailToNewsletter) {
                    const status =
                        response?.data?.subscribeEmailToNewsletter?.status!
                    let message = ''

                    if (status === SubscriptionStatusesEnum.Subscribed) {
                        message = t('footer|Thank you for subscribing')
                    }

                    if (status === SubscriptionStatusesEnum.NotActive) {
                        message = t(
                            'footer|The subscription requires a confirmation. A confirmation email has been sent to specified email address to confirm the subscription.'
                        )
                    }

                    openToast(
                        t('{{ statusText }}', {
                            statusText: message || status
                        }),
                        ToastType.Success,
                        true
                    )

                    setDirty(false)
                    setEmail('')
                    setName('')
                    setLastName('')
                    setBirthday('')
                    setFormErrors({} as SubscribeFormErrors)
                }
            } catch (e) {
                openToast(
                    t('footer|Something went wrong. Please try again'),
                    ToastType.Danger,
                    true
                )
            }
        } else {
            setFormErrors(errors)
            setButtonDisabled(hasErrors)
            setDirty(hasErrors)
        }
    }

    const handleValidation = React.useCallback(() => {
        if (dirty) {
            const { errors, hasErrors } = validateForm(
                {
                    name,
                    lastName,
                    email,
                    birthday
                },
                t
            )
            setFormErrors(errors)
            setButtonDisabled(hasErrors)
        }
    }, [birthday, dirty, email, name, t])

    React.useEffect(() => {
        handleValidation()
    }, [handleValidation])

    return (
        <>
            {hideHeading ? null : (
                <div className="text-center mb-8 md:text-left md:mb-8">
                    <h5 className="uppercase mb-2">
                        {t('footer|subscribe email')}
                    </h5>
                </div>
            )}

            <form
                onSubmit={handleSubscribe}
                className="mb-8 md:mb-0 flex flex-col w-full"
                noValidate
            >
                <div className="flex gap-4 mb-7">
                    <div className="form-group flex-1">
                        <FormInput
                            type="text"
                            id="name"
                            name={t('footer|name')}
                            value={name}
                            onChange={setName}
                            className="bg-t-2"
                            error={formErrors?.name}
                        />
                    </div>
                    <div className="form-group flex-1">
                        <FormInput
                            type="text"
                            id="lastName"
                            name={t('footer|lastName')}
                            value={lastName}
                            onChange={setLastName}
                            className="bg-t-2"
                            error={formErrors?.lastName}
                        />
                    </div>
                </div>
                <div className="flex gap-4 mb-7">
                    <div
                        className={cn(s['birthday-filed'], 'form-group flex-1')}
                    >
                        <FormInput
                            type="text"
                            id="birthday"
                            name={t('auth|Birthday')}
                            value={birthday}
                            maxLength={5}
                            onChange={handleBirthdayChange}
                            className="bg-t-2"
                            error={formErrors?.birthday}
                        />
                    </div>
                </div>
                <div className="form-group">
                    <FormInput
                        type="email"
                        id="footerEmail"
                        name={t('auth|Email')}
                        value={email}
                        onChange={setEmail}
                        className="bg-t-2"
                        error={formErrors?.email}
                    />
                </div>

                <Button
                    className="mt-8 w-full"
                    variant="secondary"
                    type="submit"
                    loading={isLoading}
                    disabled={buttonDisabled}
                >
                    {!isLoading && t('footer|subscribe')}
                </Button>
            </form>
        </>
    )
}

export default Newsletter
